import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from 'assets/icons';
import { useBlogService } from 'hooks/api/agents/content/blog';
import { useContentJobService } from 'hooks/api/agents/content/content-job';
import { isHtml, markdownToHtml } from './markdown';
import { ChannelReviewPanel } from './channel';
import { BlogRejectModal } from './rejection';
import styles from './view.module.css';

const STATUS_LABELS = { published: 'Published', draft: 'Draft', archived: 'Archived' };
const STATUS_CLASS = {
  published: 'statusPublished',
  draft: 'statusDraft',
  archived: 'statusArchived'
};

const formatDate = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
};

const readingTime = (html) => {
  if (!html) return null;
  const words = html
    .replace(/<[^>]+>/g, ' ')
    .split(/\s+/)
    .filter(Boolean).length;
  const mins = Math.max(1, Math.round(words / 200));
  return `${mins} min read`;
};

const ACTIONS = [
  { key: 'accept', label: 'Accept', icon: 'check_circle', className: 'actionApprove' },
  {
    key: 'regenerate_image',
    label: 'Regenerate image',
    icon: 'refresh',
    className: 'actionRegenerate'
  },
  { key: 'reject', label: 'Reject', icon: 'cancel', className: 'actionReject' }
];

const CONTENT_TABS = [
  { key: 'article', label: 'Blog article' },
  { key: 'email', label: 'Email', agent_type: 'email' },
  { key: 'whatsapp', label: 'WhatsApp', agent_type: 'whatsapp' },
  { key: 'social', label: 'Social', agent_type: 'social' }
];

export const BlogViewPage = ({
  blog = {},
  refresh_key = 0,
  onEditModeChange = () => {},
  onRefreshComplete = () => {}
}) => {
  const [blog_data, setBlogData] = useState(blog);
  const {
    id,
    topic,
    description,
    content,
    status,
    created_on,
    author,
    tags,
    cover_image_url: cover_image
  } = blog_data;
  const author_name = typeof author === 'object' ? author?.name || author?.email : author;
  const { acceptBlog, rejectBlog, regenerateBlogImage, fetchBlog, updateBlogDraft } =
    useBlogService();
  const { fetchContentJobsByBlog } = useContentJobService();

  const [menu_open, setMenuOpen] = useState(false);
  const [acting, setActing] = useState(null);
  const [show_reject_modal, setShowRejectModal] = useState(false);
  const [is_editing, setIsEditing] = useState(false);
  const [active_content_tab, setActiveContentTab] = useState('article');
  const [draft_topic, setDraftTopic] = useState('');
  const [draft_content, setDraftContent] = useState('');
  const [edit_error, setEditError] = useState('');
  const menu_ref = useRef(null);
  const editor_ref = useRef(null);
  const active_blog_id_ref = useRef(blog?.id || null);

  const [content_jobs, setContentJobs] = useState([]);
  const [jobs_loading, setJobsLoading] = useState(false);

  useEffect(() => {
    if (!menu_open) return;
    const onOutsideClick = (e) => {
      if (menu_ref.current && !menu_ref.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onOutsideClick);
    return () => document.removeEventListener('mousedown', onOutsideClick);
  }, [menu_open]);

  useEffect(() => {
    setBlogData(blog || {});
    active_blog_id_ref.current = blog?.id || null;
    setMenuOpen(false);
    setShowRejectModal(false);
    setIsEditing(false);
    setDraftTopic('');
    setDraftContent('');
    setEditError('');
    setActing(null);
    setActiveContentTab('article');
    setContentJobs([]);
    if (blog?.id) handleContentJobsRequest(blog.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blog]);

  useEffect(() => {
    if (!refresh_key) return;
    handleRefreshRequest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh_key]);

  const handleRefreshRequest = async () => {
    const target_blog_id = id;
    if (!target_blog_id) return onRefreshComplete();

    await Promise.all([
      refreshBlogData({ blogId: target_blog_id }),
      handleContentJobsRequest(target_blog_id)
    ]);
    onRefreshComplete();
  };

  const handleContentJobsRequest = async (blog_id) => {
    setJobsLoading(true);
    const { content_jobs } = await fetchContentJobsByBlog(blog_id);
    if (active_blog_id_ref.current === blog_id) setContentJobs(content_jobs);
    setJobsLoading(false);
  };

  useEffect(() => {
    onEditModeChange(is_editing);
  }, [is_editing, onEditModeChange]);

  useEffect(() => {
    if (!is_editing) return;
    const tab_content = content || '';
    setDraftTopic(topic || '');
    setDraftContent(isHtml(tab_content) ? tab_content : markdownToHtml(tab_content || ''));
  }, [is_editing, active_content_tab, topic, content, blog_data]);

  useEffect(() => {
    if (!is_editing || !editor_ref.current) return;
    if (editor_ref.current.innerHTML !== draft_content) {
      editor_ref.current.innerHTML = draft_content || '';
    }
  }, [draft_content, is_editing]);

  const refreshBlogData = async ({ retries = 1, delay = 0, blogId = id } = {}) => {
    if (!blogId) return false;

    for (let attempt = 0; attempt < retries; attempt += 1) {
      const { blog: latest_blog } = await fetchBlog(blogId);
      if (latest_blog) {
        if (active_blog_id_ref.current !== blogId) return false;
        setBlogData(latest_blog);
        return true;
      }

      if (delay > 0 && attempt < retries - 1) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }

    return false;
  };

  const rendered_html = useMemo(() => {
    const tab_content = content || '';
    if (!tab_content) return '';
    return isHtml(tab_content) ? tab_content : markdownToHtml(tab_content);
  }, [active_content_tab, content, blog_data]);

  const closeRejectModal = () => {
    setShowRejectModal(false);
  };

  const handleCancelEdit = () => {
    const tab_content = content || '';
    setEditError('');
    setDraftTopic(topic || '');
    setDraftContent(isHtml(tab_content) ? tab_content : markdownToHtml(tab_content || ''));
    setIsEditing(false);
  };

  const handleEditToggle = async () => {
    const target_blog_id = id;

    if (!is_editing) {
      const tab_content = content || '';
      setMenuOpen(false);
      setEditError('');
      setDraftTopic(topic || '');
      setDraftContent(isHtml(tab_content) ? tab_content : markdownToHtml(tab_content || ''));
      setIsEditing(true);
      return;
    }

    const next_topic = draft_topic.trim();
    const next_content = draft_content.trim();
    const next_content_plain = next_content
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    if (!next_topic) {
      setEditError('Title is required.');
      return;
    }

    if (!next_content_plain) {
      setEditError('Body is required.');
      return;
    }

    setEditError('');
    setActing('save');
    const payload = {
      topic: next_topic,
      content: next_content
    };

    const { success, blog: updated_blog } = await updateBlogDraft(target_blog_id, payload);

    if (success && active_blog_id_ref.current === target_blog_id) {
      if (updated_blog) setBlogData(updated_blog);
      await refreshBlogData({ blogId: target_blog_id });
      setIsEditing(false);
    }

    setActing(null);
  };

  const applyEditorFormat = (command, value = null) => {
    if (!editor_ref.current) return;
    editor_ref.current.focus();
    document.execCommand(command, false, value);
    setDraftContent(editor_ref.current.innerHTML);
  };

  const handleInsertLink = () => {
    const value = window.prompt('Enter URL');
    if (!value) return;
    applyEditorFormat('createLink', value);
  };

  const handleContentTabChange = (tab_key) => {
    if (active_content_tab === tab_key) return;
    if (is_editing) return;
    setActiveContentTab(tab_key);
  };

  const handleRejectSubmit = async (reason) => {
    const target_blog_id = id;
    setActing('reject');
    const success = await rejectBlog(target_blog_id, reason);

    if (success && active_blog_id_ref.current === target_blog_id) {
      await refreshBlogData({ blogId: target_blog_id });
    }

    setActing(null);
    return success;
  };

  const active_tab_config = CONTENT_TABS.find((tab) => tab.key === active_content_tab);
  const is_article_tab = active_content_tab === 'article';
  const active_content_job =
    content_jobs.find((job) => job.agent_type === active_tab_config?.agent_type) || null;

  const handleAction = async (action_key) => {
    const target_blog_id = id;
    setMenuOpen(false);

    if (action_key === 'reject') {
      setShowRejectModal(true);
      return;
    }

    setActing(action_key);
    if (action_key === 'accept') {
      const success = await acceptBlog(target_blog_id);
      if (success && active_blog_id_ref.current === target_blog_id) {
        await refreshBlogData({ blogId: target_blog_id });
      }
    } else if (action_key === 'regenerate_image') {
      const success = await regenerateBlogImage(target_blog_id);
      if (success && active_blog_id_ref.current === target_blog_id) {
        await refreshBlogData({ retries: 3, delay: 1200, blogId: target_blog_id });
      }
    }
    setActing(null);
  };

  return (
    <div className={styles.wrapper}>
      <header className={styles.header}>
        <div className={styles.headerRow}>
          <div className={styles.meta}>
            {status && (
              <span
                className={`${styles.statusBadge} ${styles[STATUS_CLASS[status] || 'statusDraft']}`}
              >
                {STATUS_LABELS[status] || status}
              </span>
            )}
            {created_on && <span className={styles.metaItem}>{formatDate(created_on)}</span>}
            {author_name && (
              <span className={styles.metaItem}>
                <span className={styles.metaDot} />
                {author_name}
              </span>
            )}
            {rendered_html && (
              <span className={styles.metaItem}>
                <span className={styles.metaDot} />
                {readingTime(rendered_html)}
              </span>
            )}
          </div>

          {is_article_tab && (
            <div className={styles.actionsWrapper} ref={menu_ref}>
              <button className={styles.editBtn} onClick={handleEditToggle} disabled={!!acting}>
                {acting === 'save' ? 'Saving…' : is_editing ? 'Save' : 'Edit'}
              </button>
              {is_editing && (
                <button className={styles.cancelBtn} onClick={handleCancelEdit} disabled={!!acting}>
                  Cancel
                </button>
              )}
              <button
                className={styles.actionsBtn}
                onClick={() => setMenuOpen((o) => !o)}
                disabled={!!acting || is_editing}
              >
                <span>{acting ? 'Processing…' : 'Actions'}</span>
                <span
                  className={`${styles.actionsBtnChevron} ${
                    menu_open ? styles.actionsBtnChevronOpen : ''
                  }`}
                >
                  <Icon name="expand" width={16} height={16} />
                </span>
              </button>

              {menu_open && (
                <div className={styles.actionsMenu}>
                  {ACTIONS.map(({ key, label, icon, className }) => (
                    <button
                      key={key}
                      className={`${styles.actionsMenuItem} ${styles[className]}`}
                      onClick={() => handleAction(key)}
                    >
                      <span className={styles.actionsMenuIcon}>
                        <Icon name={icon} width={16} height={16} />
                      </span>
                      {label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {is_editing ? (
          <input
            className={styles.titleInput}
            value={draft_topic}
            onChange={(e) => {
              setDraftTopic(e.target.value);
              if (edit_error) setEditError('');
            }}
            placeholder="Blog title"
          />
        ) : (
          <h1 className={styles.title}>{topic || 'Untitled'}</h1>
        )}
        {active_content_tab === 'article' && description && (
          <p className={styles.description}>{description}</p>
        )}
        {is_editing && edit_error && <div className={styles.editError}>{edit_error}</div>}

        {active_content_tab === 'article' && tags?.length > 0 && (
          <div className={styles.tags}>
            {tags.map((tag, i) => (
              <span key={i} className={styles.tag}>
                {tag}
              </span>
            ))}
          </div>
        )}

        <div className={styles.contentTabs}>
          {CONTENT_TABS.map((tab) => (
            <button
              key={tab.key}
              className={`${styles.contentTabBtn} ${
                active_content_tab === tab.key ? styles.contentTabBtnActive : ''
              }`}
              onClick={() => handleContentTabChange(tab.key)}
              disabled={is_editing}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </header>

      {is_article_tab &&
        (cover_image ? (
          <div className={styles.coverWrapper}>
            <img src={cover_image} alt={topic || 'Cover'} className={styles.coverImage} />
          </div>
        ) : (
          <hr className={styles.divider} />
        ))}

      <div className={styles.body}>
        {!is_article_tab ? (
          <ChannelReviewPanel
            blog_id={id}
            blog_status={status}
            agent_type={active_tab_config?.agent_type}
            channel_label={active_tab_config?.label}
            content_job={active_content_job}
            loading={jobs_loading}
            refresh_key={refresh_key}
            onJobsChange={() => handleContentJobsRequest(id)}
          />
        ) : is_editing ? (
          <div className={styles.editorShell}>
            <div className={styles.editorToolbar}>
              <button
                type="button"
                className={styles.editorToolBtn}
                onClick={() => applyEditorFormat('bold')}
              >
                B
              </button>
              <button
                type="button"
                className={styles.editorToolBtn}
                onClick={() => applyEditorFormat('italic')}
              >
                I
              </button>
              <button
                type="button"
                className={styles.editorToolBtn}
                onClick={() => applyEditorFormat('underline')}
              >
                U
              </button>
              <button
                type="button"
                className={styles.editorToolBtn}
                onClick={() => applyEditorFormat('formatBlock', '<h1>')}
              >
                H1
              </button>
              <button
                type="button"
                className={styles.editorToolBtn}
                onClick={() => applyEditorFormat('formatBlock', '<h2>')}
              >
                H2
              </button>
              <button
                type="button"
                className={styles.editorToolBtn}
                onClick={() => applyEditorFormat('formatBlock', '<h3>')}
              >
                H3
              </button>
              <button
                type="button"
                className={styles.editorToolBtn}
                onClick={() => applyEditorFormat('formatBlock', '<p>')}
              >
                P
              </button>
              <button
                type="button"
                className={styles.editorToolBtn}
                onClick={() => applyEditorFormat('insertUnorderedList')}
              >
                • List
              </button>
              <button
                type="button"
                className={styles.editorToolBtn}
                onClick={() => applyEditorFormat('insertOrderedList')}
              >
                1. List
              </button>
              <button type="button" className={styles.editorToolBtn} onClick={handleInsertLink}>
                Link
              </button>
            </div>
            <div
              ref={editor_ref}
              className={styles.contentEditor}
              contentEditable
              suppressContentEditableWarning
              onInput={(e) => {
                setDraftContent(e.currentTarget.innerHTML);
                if (edit_error) setEditError('');
              }}
            />
          </div>
        ) : rendered_html ? (
          <div className={styles.content} dangerouslySetInnerHTML={{ __html: rendered_html }} />
        ) : (
          <div className={styles.noContent}>No content available for this blog.</div>
        )}
      </div>

      <BlogRejectModal
        show={show_reject_modal}
        onClose={closeRejectModal}
        onSubmit={handleRejectSubmit}
        submitting={acting === 'reject'}
      />
    </div>
  );
};
